import { useCallback, useState } from "react";
import moment from "moment";
import Button from "../../Components/Button";
import DatePicker from "../../Components/DatePicker";
import TimePicker from "../../Components/TimePicker";
import Cell from "../../Tables/TableTemplate/Cell";
import { getKaminMonitoring, KaminMonitoringRow } from "../../Requests/kamin2leg";
import { printPriceWithDecimalsForTableCell } from "../../helper";
import { toJalaliDateTime } from "../../Utils/date";
import KaminTable, { KaminColumn } from "./KaminTable";
import { statusNames } from "./fields";
import KaminCriterionDiagram from "./KaminCriterionDiagram";
import KaminCycleDetails from "./KaminCycleDetails";
import { competitionStatusNames, getKaminCompetitionStatus, getKaminRepriceText } from "./competitionStatus";
import { kaminValidityNames, kaminCancellationNames, kaminPlacementNames } from "./cycleResult";

const number = (value?: number | null) => value == null ? "—" : printPriceWithDecimalsForTableCell(value);
const text = (value?: string | null) => <Cell clonedRow={{ value: value || "—" }} headerName="Value" type="Normal" />;
const date = (value?: string | null) => text(value ? toJalaliDateTime(value) : undefined);
const labels: Record<string, string> = {
  AheadFloorThresholdsMissing: "درصدهای حجم جلو کف کمین تنظیم نشده‌اند",
  AheadFloorAboveMaximum: "حجم جلو کف کمین بیشتر از حداکثر است",
  AbsoluteLoser: "شکست خورده مطلق",
  ImbalanceLimitExceeded: "تعلیق کمین؛ ناترازی تأییدشده بیشتر از سقف است",
  AheadFloorBelowInsensitivity: "حجم جلو کف کمین کمتر از درصد بی‌حسی است",
  AheadFloorInsensitive: "بی‌حس نسبت به رقابت",
  FloorPriceUnavailable: "قیمت کف کمین در دسترس نیست", CreateHuntTrigger: "ثبت تریگر انجام کمین",
  PersistedWithFill: "همراه انجام کمین ثبت شد", IndependentHuntSelected: "انتخاب شکار مستقل",
  NoCompetitionBoundary: "حجم کافی برای تعیین مرز رقبا وجود ندارد",
  NoAction: "بدون اقدام", NoOpenKaminOrders: "سفارش باز کمین وجود ندارد", OpenKaminValid: "سفارش کمین معتبر است",
  MultipleKaminPrices: "لغو کمین در چند قیمت", RetryKaminCancellation: "تلاش مجدد برای لغو کمین",
  InvalidKaminMarket: "اردربوک نامعتبر", InvalidKaminPriceTickOrRange: "قیمت خارج از تیک یا دامنه مجاز",
  OutsideSensitivityRange: "خروج از بازه عدم حساسیت", UnsentOutsideTargetRange: "درخواست ارسال‌نشده خارج از بازه هدف",
  ExcessOpenKaminVolume: "حجم باز بیشتر از حد مجاز", CompetitionBehindBoundary: "قیمت کمین بدتر از قیمت مرز رقبا پس از مهلت",
  KaminCancellationPending: "انتظار تأیید لغو کمین", AwaitingFillConfirmation: "انتظار تأیید معامله",
  AwaitingBrokerConfirmation: "انتظار تأیید کارگزاری", KaminPlacementAwaitingConfirmation: "انتظار تأیید پیش از سفارش‌گذاری",
  KaminPlacementMultiplePrices: "توقف سفارش‌گذاری به علت چند قیمت", HuntStillOpen: "شکار باز است",
  RejectionBackoff: "انتظار پس از رد درخواست", NoRemainingVolume: "حجم باقیمانده صفر است",
  NoTargetPriceTick: "نبود تیک مجاز در بازه هدف", NewVolumeBlocked: "حجم تازه مجاز نیست",
  RefillNotRequired: "نیازی به تکمیل حجم نیست", NoVolumeToAdd: "حجم قابل اضافه‌کردن وجود ندارد",
  RefillAtExistingPrice: "تکمیل حجم کمین (Refill) در همان قیمت", Refill: "تکمیل حجم کمین (Refill)", PlaceNewKaminBlock: "سفارش‌گذاری بلوک کمین",
  RetryValidatedKaminSubmission: "ادامه ارسال درخواست معتبر", ReplaceExistingRemainderWithoutIncreasingVolume: "جایگزینی باقیمانده بدون افزایش حجم",
  HuntSelected: "انتخاب شکار", HuntCapacityUnavailable: "حجم یا ظرفیت شکار کافی نیست",
  EntryDisabled: "ورود سفارش متوقف است", OrderDayExpired: "اعتبار روز سفارش پایان یافته",
  SettlePartiallyFilledHunt: "تعیین تکلیف شکار با انجام جزئی", HuntPriceOrTimeExpired: "پایان اعتبار قیمت یا مهلت شکار",
  RefillAwaitingFillConfirmation: "تکمیل حجم منتظر تأیید معامله", CycleFailed: "پردازش کامل نشد",
  WorkerErrorCleanup: "لغو سفارش‌ها پس از خطای پردازش", Create: "ثبت درخواست", Submit: "ارسال",
  Cancel: "لغو", Reconcile: "تطبیق کارگزاری", TaskStateChanged: "تغییر وضعیت تسک",
  Edit: "ویرایش", Play: "ادامه", Pause: "توقف", Delete: "حذف",
  Kamin: "کمین", Hunt: "شکار", Hedge: "رفع ناترازی", Queued: "درخواست ثبت‌شده",
  Pending: "باز", Unknown: "نامشخص", AwaitingFill: "انتظار تأیید معامله", Done: "انجام‌شده",
  Cancelled: "لغوشده", Rejected: "ردشده", Persisted: "ثبت‌شده", Attempted: "اقدام شد",
  Exception: "خطا", AwaitingConfirmation: "انتظار تأیید", Confirmed: "تأییدشده",
};
function label(value: string): string {
  if (value.startsWith("InvalidMarket: ")) return `بازار نامعتبر: ${value.slice(15)}`;
  if (value.startsWith("Operation: ")) return `فرمان: ${label(value.slice(11))}`;
  return labels[value] || statusNames[value] || value;
}

const cancellationReason = (required: boolean | null | undefined, row: KaminMonitoringRow) => {
  if (required === false) return "لغو لازم نیست";
  if (row.kaminValidity === "Invalid" && row.kaminValidityReason)
    return row.kaminValidityReason.replace(/عقب‌تر/g, "بدتر");
  if (required == null) return undefined;
  const operation = row.decisions?.find(value => value.startsWith("Operation: "));
  if (operation) return `لغو سفارش قبلی برای اجرای فرمان ${label(operation.slice(11))} تسک.`;
  if (row.decisions?.includes("OrderDayExpired")) return "روز ایجاد تسک پایان یافته است.";
  if (row.decisions?.includes("NoRemainingVolume")) return "حجم باقیماندهٔ تسک صفر است.";
  const market = row.decisions?.find(value => value.startsWith("InvalidMarket: "));
  if (market) return label(market);
  if (row.decisions?.includes("EntryDisabled")) return "ورود کمین متوقف شده است.";
  if (row.decisions?.includes("WorkerErrorCleanup")) return "لغو سفارش کمین پس از خطای پردازش.";
  return "سفارش فلگ لغو دارد؛ علت اولیه در این رکورد ثبت نشده است.";
};
const renderCancellationReason = (value: boolean | null | undefined, row: KaminMonitoringRow) =>
  <div className="max-w-sm whitespace-normal">{text(cancellationReason(value, row))}</div>;

const numericColumns = [
  ["orderPriceCount", "تعداد قیمت سفارش"], ["blockVolume", "حجم بلوک (بسته)"],
  ["nativeBlockVolume", "حجم بلوک (نماد)"], ["orderPrice", "قیمت کمین باز"],
  ["orderCriterion", "مقدار معیار کمین"],
  ["openOrderVolume", "حجم باز سفارش (نماد)"], ["openOrderUnits", "حجم باز (بسته)"],
  ["huntTopPrice", "قیمت سرخط شکار"],
  ["huntTopVolume", "حجم سرخط شکار"], ["percentage", "درصد معیار"],
  ["idealKaminCompetitionPrice", "قیمت کمین ایده‌آل رقابت"],
  ["lowerSensitivityPrice", "قیمت مرز پایین حساسیت"], ["upperSensitivityPrice", "قیمت مرز بالای حساسیت"],
  ["lowerSensitivityCriterion", "کف معیار عدم حساسیت"], ["upperSensitivityCriterion", "سقف معیار عدم حساسیت"],
  ["floorReturnPercent", "درصد کف بازده"], ["ceilingReturnPercent", "درصد سقف بازده"],
  ["targetLower", "کف معیار هدف"], ["targetUpper", "سقف معیار هدف"],
  ["floorKaminPrice", "کف قیمت کمین"], ["ceilingKaminPrice", "سقف قیمت کمین"],
  ["competitorBoundaryPrice", "قیمت مرز رقبا"], ["idealCompetitionPrice", "قیمت ایده‌آل رقابت"],
  ["selectedKaminPrice", "قیمت انتخاب‌شده"], ["volumeAheadOfFloor", "حجم جلو کف کمین"],
  ["volumeAheadOfFloorPercent", "حجم جلو کف / بلوک (%)"], ["minimumAheadVolume", "حداقل حجم تجمیعی جلو"],
  ["maxAheadFloorPercent", "حداکثر حجم جلو کف کمین (%)"], ["aheadFloorInsensitivityPercent", "% بی‌حسی حجم جلو کف کمین"],
  ["aheadTicks", "تعداد تیک جلوتر"], ["competitionSeconds", "حساسیت رقابت (ثانیه)"],
  ["refillThreshold", "آستانه پایین حجم"], ["totalVolume", "حجم کل"],
  ["filledUnits1", "انجام‌شده لگ اول"], ["filledUnits2", "انجام‌شده لگ دوم"],
  ["imbalanceUnits", "ناترازی"], ["maxImbalancePercent", "سقف ناترازی (%)"],
  ["huntMaxCost", "حداکثر ب.ت شکار"], ["finalHedgeMaxCost", "حداکثر ب.ت رفع ناترازی"],
  ["hedgeTimeoutSeconds", "مهلت رفع ناترازی (ثانیه)"],
];
const detailColumns: KaminColumn[] = [
  { title: "زمان", dataIndex: "timeUtc", render: date },
  { title: "زمان دور اجرا", dataIndex: "executionCycleTimeUtc", render: date },
  { title: "تسک", dataIndex: "idTask", render: number },
  { title: "اعتبار کمین این دور", dataIndex: "kaminValidity", render: value => text(value ? kaminValidityNames[value] || value : "در این رکورد ثبت نشده") },
  { title: "دلیل نامعتبرشدن / عدم بررسی", dataIndex: "kaminValidityReason", render: value => <div className="max-w-sm whitespace-normal">{text(value?.replace(/عقب‌تر/g, "بدتر"))}</div> },
  { title: "نتیجه لغو کمین", dataIndex: "kaminCancellation", render: value => text(value ? kaminCancellationNames[value] || value : "در این رکورد ثبت نشده") },
  { title: "نتیجه سفارش‌گذاری کمین", dataIndex: "kaminPlacement", render: value => text(value ? kaminPlacementNames[value] || value : "در این رکورد ثبت نشده") },
  { title: "دلیل عدم سفارش‌گذاری / عدم تأیید", dataIndex: "kaminPlacementReason", render: value => <div className="max-w-sm whitespace-normal">{text(value)}</div> },
  { title: "آیا احتیاج به لغو است", dataIndex: "requiresKaminCancellation", render: renderCancellationReason },
  { title: "آیا احتیاج به refill است", dataIndex: "requiresKaminRefill", render: value => text(value == null ? undefined : value ? "بله" : "خیر") },
  { title: "آیا احتیاج به سفارش گذاری با تغییر قیمت است", dataIndex: "requiresKaminReprice", render: (_, row) => text(getKaminRepriceText(row)) },
  { title: "دلیل نداشتن سفارش باز کمین", dataIndex: "noOrderReason", render: value => <div className="max-w-sm whitespace-normal">
    {text(value === "نتیجهٔ دور اجرای این تسک هنوز ثبت نشده است." ? undefined : value)}</div> },
  { title: "تصمیم گرفته‌شده", render: (_, row: KaminMonitoringRow) => <div className="max-w-sm whitespace-normal">
    {text((row.decisions?.length ? row.decisions : (row.decisionTaken || "").split("; "))
      .filter(decision => decision !== "ExecutionCycleNotObserved" && decision !== "PeriodicObservation").map(label).join("؛ "))}</div> },
  { title: "نوع ثبت", dataIndex: "trigger", render: (value, row: KaminMonitoringRow) => text(row.decisions?.includes("PeriodicObservation")
    ? "پایش دوره‌ای" : ({ Action: "اقدام", Periodic: "دور اجرا", Error: "خطا" } as Record<string, string>)[value] || value) },
  { title: "معیار", dataIndex: "criterionType", render: value => text(value === "SignedCost" ? "بهای تمام‌شده" : value) },
  { title: "واحد معیار", dataIndex: "criterionUnit", render: value => text(value === "Percent" ? "درصد" : value === "Price" ? "بها" : value) },
  { title: "وضعیت رقابت", dataIndex: "competitionStatus", render: (_, row: KaminMonitoringRow) => {
    const status = getKaminCompetitionStatus(row);
    return text(status ? competitionStatusNames[status] : undefined);
  } },
  ...numericColumns.map(([dataIndex, title]) => ({ title, dataIndex, render: number })),
  { title: "قیمت‌های سفارش", dataIndex: "orderPrices", render: (values: number[]) => <>{values?.map((value, index) => <span key={value}>{index > 0 ? "، " : ""}{number(value)}</span>)}</> },
  { title: "زمان اولین ارسال", dataIndex: "firstPlacedAt", render: date },
  { title: "نتیجه اقدامات", render: (_, row: KaminMonitoringRow) => <div className="max-w-sm whitespace-normal">{row.actions?.map((action, index) =>
    <div key={index}>{text(`${label(action.kind)}${action.purpose ? ` ${label(action.purpose)}` : ""}: ${label(action.outcome)}`)}
      {action.price != null && <span> · قیمت {number(action.price)}</span>}
      {action.volume != null && <span> · حجم {number(action.volume)}</span>}</div>)}</div> },
  { title: "وضعیت", dataIndex: "status", render: value => text(label(value)) },
  { title: "هشدار", dataIndex: "warning", render: text },
  { title: "اعتبار اردربوک", dataIndex: "marketInvalidReason", render: value => text(value === "MarketSnapshotUnavailable" ? "اردربوک در دسترس نیست" : value || "معتبر") },
  { title: "خطا", dataIndex: "error", render: text },
];

const yesNo = (value?: boolean | null) => text(value == null ? undefined : value ? "بله" : "خیر");
const columns: KaminColumn[] = [
  { title: "زمان", dataIndex: "timeUtc", render: date },
  { title: "قیمت سرخط شکار", dataIndex: "huntTopPrice", render: number },
  { title: "قیمت کف حساسیت", dataIndex: "lowerSensitivityPrice", render: number },
  { title: "قیمت کف کمین", dataIndex: "floorKaminPrice", render: number },
  { title: "قیمت رقبا", dataIndex: "competitorBoundaryPrice", render: number },
  { title: "قیمت ایده آل رقبا", dataIndex: "idealCompetitionPrice", render: number },
  { title: "قیمت سقف کمین", dataIndex: "ceilingKaminPrice", render: number },
  { title: "قیمت کمین شده", dataIndex: "orderPrice", render: number },
  { title: "حجم کمین شده", dataIndex: "openOrderVolume", render: number },
  { title: "قیمت سقف حساسیت", dataIndex: "upperSensitivityPrice", render: number },
  { title: "وضعیت رقابت", dataIndex: "competitionStatus", render: (_, row: KaminMonitoringRow) => {
    const status = getKaminCompetitionStatus(row);
    return text(status ? competitionStatusNames[status] : undefined);
  } },
  { title: "آیا احتیاج به لغو است", dataIndex: "requiresKaminCancellation", render: renderCancellationReason },
  { title: "آیا احتیاج به refill است", dataIndex: "requiresKaminRefill", render: yesNo },
  { title: "آیا احتیاج به سفارش گذاری با تغییر قیمت است", dataIndex: "requiresKaminReprice", render: (_, row) => text(getKaminRepriceText(row)) },
  { ...detailColumns.find(column => column.title === "نتیجه اقدامات")!, title: "اقدامات انجام شده" },
];

export default function KaminMonitoring({ orderId }: { orderId: number }) {
  const [latest, setLatest] = useState<KaminMonitoringRow>();
  const [selected, setSelected] = useState<KaminMonitoringRow>();
  const [details, setDetails] = useState<KaminMonitoringRow>();
  const [initialFrom] = useState(() => moment().subtract(20, "minutes").startOf("minute"));
  const [fromDate, setFromDate] = useState(() => initialFrom.format("YYYY-MM-DD"));
  const [fromTime, setFromTime] = useState<string | null>(() => initialFrom.toISOString());
  const [search, setSearch] = useState(() => ({ fromDateTime: initialFrom.toISOString(), revision: 0 }));
  const load = useCallback(async (offset: number, pageSize: number, signal: AbortSignal) => {
    const page = await getKaminMonitoring(orderId, search.fromDateTime, offset, pageSize, signal);
    if (!signal.aborted && offset === 0) setLatest(page.items[0]);
    return page;
  }, [orderId, search.fromDateTime]);
  const apply = () => {
    const time = fromTime ? moment(fromTime) : moment().startOf("day");
    const from = moment(fromDate, "YYYY-MM-DD", true).hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
    if (from.isValid()) {
      setLatest(undefined); setSelected(undefined);
      setSearch(previous => ({ fromDateTime: from.toISOString(), revision: previous.revision + 1 }));
    }
  };
  return <div className="flex flex-col gap-3 w-full min-w-0 p-3">
    <div className="text-sky-900">کمین · تسک {number(orderId)}</div>
    <div className="flex flex-wrap gap-3 items-end">
      <div className="w-44"><DatePicker name="fromDate" label="از تاریخ" value={fromDate} onChange={(_, value) => setFromDate(value)} /></div>
      <div className="w-36"><TimePicker name="fromTime" placeholder="از ساعت" value={fromTime}
        onChange={(_, value) => setFromTime(value.ISOString)} extraLabel /></div>
      <div className="w-28"><Button text="جستجو" variant="contained" onClick={apply} disabled={!moment(fromDate, "YYYY-MM-DD", true).isValid()} /></div>
    </div>
    <KaminCriterionDiagram snapshot={selected || latest} />
    <KaminTable key={search.revision} columns={[...columns,
      { title: "عملیات", render: (_, row: KaminMonitoringRow) => <Button text="جزئیات" variant="contained" className="table-btn" onClick={() => setDetails(row)} /> }]}
      getPage={load} onSelect={() => {}} onRowClick={setSelected} />
    {details && <KaminCycleDetails row={details} columns={detailColumns} onClose={() => setDetails(undefined)} />}
  </div>;
}
